import { useEffect } from 'react';

import { getReferralCodeFromUrl } from 'modules/referralProgram/utils/getReferralCodeFromUrl';
import { selectIsAccountEligible } from 'modules/referralProgram/store/selectors';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useAuthInstance } from 'modules/auth/hooks/useAuthInstance';
import { usePersonalAccountStatus } from 'modules/personalAccount/hooks/usePersonalAccountStatus';
import { useReferrer } from 'modules/referralProgram/hooks/useReferrer';
import { useUser } from 'modules/user/hooks/useUser';
import { useValidateReferralCode } from 'modules/referralProgram/hooks/useValidateReferralCode';

export interface IUseInitReferralFlowProps {
  handleIneligibleAccountDialogOpen: () => void;
  handleSwitchAccountDialogOpen: () => void;
  handleWelcomeDialogOpen: () => void;
  isCoverLoaded: boolean;
  isLoggingOut: boolean;
}

export const useInitReferralFlow = ({
  handleIneligibleAccountDialogOpen,
  handleSwitchAccountDialogOpen,
  handleWelcomeDialogOpen,
  isCoverLoaded,
  isLoggingOut,
}: IUseInitReferralFlowProps) => {
  const { isLoggedIn, isLoggingIn } = useAuth();
  const { isInitialized: isAuthDataInitialized } = useAuthInstance();
  const { isUserInitialized } = useUser();
  const { isPersonalAccount } = useAccount();

  const {
    isPersonalAccountFreemium,
    isPersonalAccountStatusLoaded,
    userEndpointToken,
  } = usePersonalAccountStatus({ skipFetching: !isUserInitialized });

  const isAccountEligible = useAppSelector(state =>
    selectIsAccountEligible(state, { userEndpointToken: userEndpointToken! }),
  );

  const { referralCode } = getReferralCodeFromUrl();

  const { validateReferralCode } = useValidateReferralCode();

  const { data: referrer, isSuccess: referrerLoaded } = useReferrer();

  const hasReferrer = Boolean(referrer);

  useEffect(() => {
    // TODO: rethink this logcis to get rid of nested if's
    if (isAuthDataInitialized && !isLoggingIn && !isLoggingOut) {
      const error = validateReferralCode(referralCode);

      if (!error && isCoverLoaded) {
        if (isLoggedIn) {
          if (isPersonalAccountStatusLoaded) {
            if (isAccountEligible) {
              handleWelcomeDialogOpen();
            } else if (!isPersonalAccount && isPersonalAccountFreemium) {
              handleSwitchAccountDialogOpen();
            } else {
              handleIneligibleAccountDialogOpen();
            }
          }
        } else {
          handleWelcomeDialogOpen();
        }
      }
    }
    // Only data should be tracked, not callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasReferrer,
    isAccountEligible,
    isAuthDataInitialized,
    isCoverLoaded,
    isLoggingIn,
    isLoggingOut,
    isPersonalAccountFreemium,
    isPersonalAccountStatusLoaded,
    referralCode,
    referrerLoaded,
  ]);
};
